import { DUE_BUCKETS, dueBucket, type DueBucket } from "./taskDates";
import type { TaskItem, TaskScope, TaskUrgency } from "./tarefasApi";

/**
 * A ordem e o agrupamento da lista, num sítio só. Vivem no ecrã (e não no servidor) para a lista
 * se manter certa ao criar e editar, sem recarregar.
 */

const URGENCY_RANK: Record<TaskUrgency, number> = { VeryUrgent: 2, Urgent: 1, Normal: 0 };

/** Mais urgentes primeiro; na mesma urgência, a data mais próxima (sem data no fim); depois as mais recentes. */
export function compareOpenTasks(a: TaskItem, b: TaskItem): number {
  const urgency = URGENCY_RANK[b.urgency] - URGENCY_RANK[a.urgency];
  if (urgency !== 0) return urgency;
  if (a.dueDate !== b.dueDate) {
    if (!a.dueDate) return 1;
    if (!b.dueDate) return -1;
    return a.dueDate.slice(0, 10) < b.dueDate.slice(0, 10) ? -1 : 1;
  }
  return b.created.localeCompare(a.created);
}

/** As concluídas há menos tempo primeiro. */
export function compareDoneTasks(a: TaskItem, b: TaskItem): number {
  return (b.completedAt ?? b.created).localeCompare(a.completedAt ?? a.created);
}

export interface TaskGroup {
  key: DueBucket | "done";
  label: string;
  items: TaskItem[];
  /** Quantas do grupo estão por fazer (o número do cabeçalho; as acabadas de concluir não contam). */
  openCount: number;
}

/**
 * Em aberto: por quando (Atrasadas, Hoje, …). Uma tarefa acabada de concluir (`keepInPlace`)
 * fica no sítio onde estava, riscada, até se trocar de filtro — mas já não conta no grupo.
 * As outras concluídas (âmbito "Todas") vão para um grupo próprio no fim, e não para
 * "Atrasadas" por terem uma data passada.
 */
export function groupTasks(
  tasks: readonly TaskItem[],
  scope: TaskScope,
  keepInPlace: ReadonlySet<number>,
  today: Date = new Date(),
): TaskGroup[] {
  if (scope === "done") {
    const items = [...tasks].sort(compareDoneTasks);
    return items.length > 0 ? [{ key: "done", label: "Concluídas", items, openCount: 0 }] : [];
  }

  const byBucket = new Map<DueBucket, TaskItem[]>();
  const done: TaskItem[] = [];
  for (const task of tasks) {
    if (task.status === "Done" && !keepInPlace.has(task.id)) {
      done.push(task);
      continue;
    }
    const bucket = dueBucket(task.dueDate, today);
    byBucket.set(bucket, [...(byBucket.get(bucket) ?? []), task]);
  }

  const groups: TaskGroup[] = DUE_BUCKETS.filter((bucket) => byBucket.has(bucket.key)).map((bucket) => {
    const items = (byBucket.get(bucket.key) ?? []).sort(compareOpenTasks);
    return {
      key: bucket.key,
      label: bucket.label,
      items,
      openCount: items.filter((task) => task.status !== "Done").length,
    };
  });

  if (done.length > 0) {
    groups.push({ key: "done", label: "Concluídas", items: done.sort(compareDoneTasks), openCount: 0 });
  }

  return groups;
}
